import { packageTierActionTypes as actionTypes } from '../../ActionTypes/Voss/packageTierActionTypes';
import { apiThunk } from '../../Utils/apiThunk';
import { packageResourceUrl } from './packageResourceApi';
import { toTierDraft, toUpdateTierRequest } from '../../Components/Voss/Packages/tierUtils';
import type { AppDispatch } from '../../Store/types';
import type { CreatePackageTierRequest, PackageTier, ProductName, TierRankChange, TierSaveStep } from '../../Components/Voss/Packages/Packages.types';

const tierUrl = (packageId: string, versionId: string | undefined, tierId?: string) => packageResourceUrl(packageId, 'tiers', versionId, tierId);

const productGroupUrl = (packageId: string, tierId: string, versionId: string | undefined, groupId?: string) =>
    packageResourceUrl(packageId, `tiers/${tierId}/productGroups`, versionId, groupId);

const stepRequest = (packageId: string, tierId: string, versionId: string | undefined, step: TierSaveStep): [string, RequestInit] => {
    switch (step.kind) {
        case 'updateTier':
            return [tierUrl(packageId, versionId, tierId), { method: 'PUT', body: JSON.stringify(step.body) }];
        case 'updateGroup':
            return [productGroupUrl(packageId, tierId, versionId, step.groupId), { method: 'PUT', body: JSON.stringify(step.body) }];
        case 'deleteGroup':
            return [productGroupUrl(packageId, tierId, versionId, step.groupId), { method: 'DELETE' }];
        case 'createGroup':
            return [productGroupUrl(packageId, tierId, versionId), { method: 'POST', body: JSON.stringify(step.body) }];
    }
};

const fetchTiers = (packageId: string, versionId: string | undefined) => (dispatch: AppDispatch) =>
    dispatch(apiThunk<PackageTier[]>(tierUrl(packageId, versionId), { loadingKey: 'packageTiers' })).then((data) => {
        if (data !== null) dispatch({ type: actionTypes.STORE_PACKAGE_TIERS, data: { versionId, tiers: data } });
    });

const packageTierActions = {
    fetchTiers,

    clearTiers() {
        return { type: actionTypes.CLEAR_PACKAGE_TIERS };
    },

    // its own fetch, because the store's productNames holds the license-only list the invoicing settings use
    getProductCatalog() {
        return apiThunk<ProductName[]>('/api/packages/getProductNames?allTypes=true', { loadingKey: 'productCatalog' });
    },

    // the refetch is awaited so an "Add tier" straight after reads the new rank, rather than reusing it and silently making a sidegrade
    createTier(packageId: string, model: CreatePackageTierRequest, versionId: string | undefined, onSuccess: () => void) {
        return (dispatch: AppDispatch) =>
            dispatch(apiThunk<PackageTier>(tierUrl(packageId, versionId), { init: { method: 'POST', body: JSON.stringify(model) } })).then(async (data) => {
                if (data === null) return;
                await dispatch(fetchTiers(packageId, versionId));
                onSuccess();
            });
    },

    // VOSS has no call for a tier with its groups, so a failure stops the rest; the refetch is awaited so a stale tier can't be reopened and its new groups created twice
    saveTier(packageId: string, tierId: string, steps: TierSaveStep[], versionId: string | undefined) {
        return async (dispatch: AppDispatch): Promise<'saved' | 'partial' | 'failed'> => {
            let written = 0;
            try {
                for (const step of steps) {
                    const [url, init] = stepRequest(packageId, tierId, versionId, step);
                    await dispatch(apiThunk<unknown>(url, { init, rethrow: true }));
                    written++;
                }
                return 'saved';
            } catch {
                return written === 0 ? 'failed' : 'partial';
            } finally {
                if (written > 0) await dispatch(fetchTiers(packageId, versionId));
            }
        };
    },

    // the new order shows straight away; the refetch then replaces it with what VOSS kept, also when a write failed
    reorderTiers(packageId: string, tiers: PackageTier[], changes: TierRankChange[], versionId: string | undefined) {
        return async (dispatch: AppDispatch) => {
            const newRanks = new Map(changes.map(({ tier, rank }) => [tier.id, rank]));
            dispatch({ type: actionTypes.STORE_PACKAGE_TIERS, data: { versionId, tiers: tiers.map((tier) => ({ ...tier, rank: newRanks.get(tier.id) ?? tier.rank })) } });
            try {
                for (const { tier, rank } of changes) {
                    const body = { ...toUpdateTierRequest(toTierDraft(tier)), rank };
                    await dispatch(apiThunk<PackageTier>(tierUrl(packageId, versionId, tier.id), { init: { method: 'PUT', body: JSON.stringify(body) }, rethrow: true }));
                }
            } catch {
                // apiThunk has already shown the error
            } finally {
                await dispatch(fetchTiers(packageId, versionId));
            }
        };
    },

    deleteTier(packageId: string, tierId: string, versionId: string | undefined, onSuccess: () => void) {
        return (dispatch: AppDispatch) =>
            dispatch(apiThunk<unknown>(tierUrl(packageId, versionId, tierId), { init: { method: 'DELETE' }, rethrow: true }))
                .then(async () => {
                    await dispatch(fetchTiers(packageId, versionId));
                    onSuccess();
                })
                .catch(() => undefined);
    },
};

export default packageTierActions;
